import { Play, Pause } from 'lucide-react';
import { useRadioStore } from '../../store/radioStore';
import { MoodChips } from './MoodChips';
import { AdNotice } from './AdNotice';

interface RadioHeroPlayerProps {
  profileSlug: string;
  profileName: string;
  moods: string[];
  currentMood: string | null;
}

/**
 * Above-the-fold hero player. The big Play button doubles as the
 * "unlock autoplay" gesture for browsers — on first click we call
 * radioStore.startRadio() so subsequent station switches inside the
 * same tab don't need a fresh click.
 */
export function RadioHeroPlayer({
  profileSlug,
  profileName,
  moods,
  currentMood,
}: RadioHeroPlayerProps) {
  const { isPlaying, isLoading, isAd, currentTrack, elapsed, duration } =
    useRadioStore();
  const startRadio = useRadioStore((s) => s.startRadio);
  const pause = useRadioStore((s) => s.pause);
  const resume = useRadioStore((s) => s.resume);

  const progressPct =
    duration > 0 ? Math.min(100, (elapsed / duration) * 100) : 0;

  const onTogglePlay = () => {
    if (isPlaying) {
      pause();
      return;
    }
    if (currentTrack && currentMood) {
      // Already attached — resume rather than reattach
      resume();
      return;
    }
    if (currentMood) {
      void startRadio(profileSlug, currentMood);
    }
  };

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1a1a2e] to-[#2a1a4a] p-6">
      {/* AD badge when an ad is playing */}
      {isAd && (
        <div className="absolute right-4 top-4 z-10 rounded-full bg-amber-500/20 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-amber-200">
          Sponsored
        </div>
      )}

      <div className="text-[10px] uppercase tracking-[2px] text-white/60">
        {isAd ? 'Sponsored · Fonorex' : `${profileName}${currentMood ? ` · ${currentMood}` : ''}`}
      </div>

      <div className="mt-2 text-lg font-semibold text-white">
        {currentTrack?.name || 'Fonorex Radio'}
      </div>

      <div className="mt-6 flex items-center gap-4">
        <button
          type="button"
          aria-label={isPlaying ? 'Pause' : 'Play'}
          onClick={onTogglePlay}
          disabled={isLoading}
          className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-violet-800 text-white shadow-lg hover:scale-105 transition-transform disabled:opacity-50"
        >
          {isPlaying ? <Pause size={28} /> : <Play size={28} fill="currentColor" />}
        </button>

        <div className="flex-1">
          <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
            <div
              className={`h-full transition-[width] duration-1000 ease-linear ${
                isAd
                  ? 'bg-gradient-to-r from-amber-400 to-amber-600'
                  : 'bg-gradient-to-r from-violet-400 to-pink-400'
              }`}
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      </div>

      <div className="mt-6">
        <MoodChips
          profileSlug={profileSlug}
          moods={moods}
          activeMood={currentMood}
          disabled={isAd}
        />
      </div>

      <AdNotice />
    </div>
  );
}
